/**
 * Stock Transfer To Shop Screen (9.1) — Inventory Module
 *
 * Proper transfer entry form with AE item classification columns.
 * Any attribute added in Attribute Studio automatically appears in the item grid.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, RefreshCw, AlertCircle, Package, ArrowRight } from 'lucide-react';
import { useAttributeEngine } from '../../../hooks/useAttributeEngine';
import { useT, LanguageSwitcher } from '../../../lib/manchala-i18n.react.js';
import { API_BASE, getToken } from '../../../lib/api';

const ST_ATTR_GROUPS = ['grp_classification', 'grp_style'];

const emptyItem = (aeAttrIds = []) => ({
  id: Date.now() + Math.random(),
  itemCode: '', itemDesc: '', qty: '', uom: 'PCS',
  ...Object.fromEntries(aeAttrIds.map(id => [id, ''])),
});

const today = () => new Date().toISOString().split('T')[0];

const TRANSFER_TYPES = ['Branch to Shop', 'Warehouse to Shop', 'Shop to Shop', 'Return to Warehouse'];

const StockTransferToShopScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const t = useT();
  const token = getToken();
  const { itemMasterAttrs, toLookupOptions, loading: aeLoading } = useAttributeEngine();

  // AE attrs for item grid
  const aeItemAttrs = useMemo(() =>
    itemMasterAttrs.filter(a => ST_ATTR_GROUPS.includes(a.GroupId)),
    [itemMasterAttrs]
  );
  const aeAttrIds = useMemo(() => aeItemAttrs.map(a => a.AttributeId), [aeItemAttrs]);

  // Theme helpers
  const bgColor    = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg     = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg    = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor  = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg  = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  // Form state
  const [stNo, setStNo]               = useState('ST-AUTO');
  const [stDate, setStDate]           = useState(today());
  const [transferType, setTransferType] = useState('Branch to Shop');
  const [fromBranch, setFromBranch]   = useState('');
  const [toBranch, setToBranch]       = useState('');
  const [remarks, setRemarks]         = useState('');
  const [items, setItems]             = useState([]);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [success, setSuccess]         = useState('');

  // List state
  const [listData, setListData]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [view, setView]               = useState('form'); // 'form' | 'list'
  const [searchTerm, setSearchTerm]   = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      // Mock data — replace with real API when available
      setListData([
        { id: 1, stNo: 'ST-0001', stDate: '2026-07-28', transferType: 'Branch to Shop', fromBranch: 'Main WH', toBranch: 'MG Road Shop', items: 5, status: 'Completed' },
        { id: 2, stNo: 'ST-0002', stDate: '2026-07-29', transferType: 'Warehouse to Shop', fromBranch: 'Central WH', toBranch: 'Brigade Shop', items: 3, status: 'Pending' },
      ]);
    } catch { setError('Failed to load transfer list'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const addRow = () => setItems(p => [...p, emptyItem(aeAttrIds)]);
  const removeRow = (id) => setItems(p => p.filter(r => r.id !== id));
  const updateRow = (id, patch) => setItems(p => p.map(r => r.id === id ? { ...r, ...patch } : r));

  const handleSave = async () => {
    if (!fromBranch || !toBranch) { setError('From Branch and To Branch are required'); return; }
    if (fromBranch === toBranch) { setError('From Branch and To Branch must be different'); return; }
    if (!items.length) { setError('Add at least one item'); return; }
    setSaving(true); setError(null);
    try {
      // TODO: wire to real API POST /api/stock-transfer
      await new Promise(r => setTimeout(r, 800));
      setSuccess(`Transfer ${stNo} saved successfully!`);
      setStNo(`ST-${String(Math.floor(1000 + Math.random() * 9000))}`);
      setItems([]);
    } catch (e) { setError('Failed to save transfer'); }
    finally { setSaving(false); }
  };

  const handleClear = () => { setItems([]); setFromBranch(''); setToBranch(''); setRemarks(''); setError(null); setSuccess(''); };

  const filteredList = listData.filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase())));

  const inputCls = `px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`;

  return (
    <div className={`h-full flex flex-col ${bgColor} ${textColor} p-1 overflow-auto`} data-testid="stock-transfer-to-shop-screen">

      {/* Header */}
      <div className={`${cardBg} rounded p-1.5 mb-1 border ${borderColor} flex items-center justify-between`}>
        <div>
          <h1 className="text-base font-bold">Stock Transfer To Shop</h1>
          <p className="text-xs text-gray-400">Screen ID: 9.1 | Inventory Module</p>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher theme={theme} data-testid="st-lang-switcher" />
          <button onClick={() => setView(v => v === 'form' ? 'list' : 'form')}
            className="px-3 py-1 text-xs rounded border border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
            {view === 'form' ? 'List View' : 'New Transfer'}
          </button>
        </div>
      </div>

      {error && <div className="mb-1 p-2 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-xs flex items-center gap-2"><AlertCircle size={14} />{error}<button onClick={() => setError(null)} className="ml-auto">×</button></div>}
      {success && <div className="mb-1 p-2 bg-emerald-500/20 border border-emerald-500/50 rounded text-emerald-400 text-xs">{success}</div>}

      {view === 'form' ? (
        <>
          {/* Form Header */}
          <div className={`${cardBg} rounded p-2 mb-1 border ${borderColor}`}>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <label className={`w-20 text-xs ${labelColor} shrink-0`}>ST No</label>
                <input value={stNo} readOnly className={`flex-1 ${inputCls} opacity-70 font-mono`} data-testid="st-no-input" />
              </div>
              <div className="flex items-center gap-2">
                <label className={`w-20 text-xs ${labelColor} shrink-0`}>Date</label>
                <input type="date" value={stDate} onChange={e => setStDate(e.target.value)} className={`flex-1 ${inputCls}`} data-testid="st-date-input" />
              </div>
              <div className="flex items-center gap-2">
                <label className={`w-24 text-xs ${labelColor} shrink-0`}>Transfer Type</label>
                <select value={transferType} onChange={e => setTransferType(e.target.value)} className={`flex-1 ${inputCls}`} data-testid="st-type-select">
                  {TRANSFER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="flex items-center gap-2">
                <label className={`w-20 text-xs ${labelColor} shrink-0`}>From Branch</label>
                <input value={fromBranch} onChange={e => setFromBranch(e.target.value)} placeholder="Source branch" className={`flex-1 ${inputCls}`} data-testid="st-from-branch" />
              </div>
              <div className="flex items-center justify-center text-gray-400"><ArrowRight size={16} /></div>
              <div className="flex items-center gap-2">
                <label className={`w-20 text-xs ${labelColor} shrink-0`}>To Branch</label>
                <input value={toBranch} onChange={e => setToBranch(e.target.value)} placeholder="Destination branch" className={`flex-1 ${inputCls}`} data-testid="st-to-branch" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <label className={`w-20 text-xs ${labelColor} shrink-0`}>Remarks</label>
              <input value={remarks} onChange={e => setRemarks(e.target.value)} className={`flex-1 ${inputCls}`} data-testid="st-remarks" />
            </div>
          </div>

          {/* Item Grid */}
          <div className={`${cardBg} rounded border ${borderColor} flex-1 overflow-hidden flex flex-col`}>
            <div className={`flex items-center justify-between px-2 py-1 border-b ${borderColor}`}>
              <span className="text-xs font-semibold">
                Item Details
                {aeLoading && <span className="text-gray-400 font-normal ml-2">(Loading AE attributes...)</span>}
              </span>
              <button onClick={addRow} className="px-2 py-0.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="st-add-row-btn">+ Add Row</button>
            </div>
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-xs">
                <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                  <tr>
                    <th className="px-2 py-1 text-left">Item</th>
                    {aeItemAttrs.map(attr => (
                      <th key={attr.AttributeId} className="px-2 py-1 text-left whitespace-nowrap">
                        {t(`attr.${attr.AttributeId}`, attr.Label)}
                      </th>
                    ))}
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-left">UOM</th>
                    <th className="px-2 py-1 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(row => (
                    <tr key={row.id} className={`border-t ${borderColor}`} data-testid={`st-item-row-${row.id}`}>
                      <td className="px-2 py-1" style={{ minWidth: 180 }}>
                        <input value={row.itemDesc} onChange={e => updateRow(row.id, { itemDesc: e.target.value })}
                          placeholder="Item name / code" className={`w-full ${inputCls}`} />
                      </td>
                      {aeItemAttrs.map(attr => {
                        const opts = toLookupOptions(attr.AttributeId);
                        const isSelect = attr.ControlType === 'LookUpEdit' || attr.ControlType === 'ComboBoxEdit';
                        const val = row[attr.AttributeId] || '';
                        return (
                          <td key={attr.AttributeId} className="px-2 py-1">
                            {isSelect ? (
                              <select value={val} onChange={e => updateRow(row.id, { [attr.AttributeId]: e.target.value })}
                                className={inputCls} style={{ minWidth: 80 }} data-testid={`st-attr-${attr.AttributeId}-${row.id}`}>
                                <option value="">{aeLoading ? '…' : '—'}</option>
                                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            ) : (
                              <input value={val} onChange={e => updateRow(row.id, { [attr.AttributeId]: e.target.value })}
                                className={`w-20 ${inputCls}`} />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1">
                        <input type="number" value={row.qty} onChange={e => updateRow(row.id, { qty: e.target.value })}
                          className={`w-16 text-right ${inputCls}`} />
                      </td>
                      <td className="px-2 py-1">
                        <input value={row.uom} onChange={e => updateRow(row.id, { uom: e.target.value })}
                          className={`w-14 ${inputCls}`} />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button onClick={() => removeRow(row.id)} className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`st-remove-row-${row.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr><td colSpan={4 + aeItemAttrs.length} className="px-2 py-6 text-center text-gray-500">
                      No items yet — click "+ Add Row"
                    </td></tr>
                  )}
                  {items.length > 0 && (
                    <tr className={`border-t-2 ${borderColor} font-semibold ${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}`}>
                      <td colSpan={2 + aeItemAttrs.length} className="px-2 py-1 text-right">Total Items:</td>
                      <td className="px-2 py-1 text-right">
                        {items.reduce((s, r) => s + (parseFloat(r.qty) || 0), 0).toFixed(0)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-2 mt-1">
            <button onClick={handleSave} disabled={saving} data-testid="st-save-btn"
              className="px-4 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold flex items-center gap-1.5">
              <Save size={13} />{saving ? 'Saving...' : 'Save Transfer'}
            </button>
            <button onClick={handleClear} className="px-3 py-1.5 text-xs rounded border border-white/20 hover:bg-white/5 text-white">Clear</button>
          </div>
        </>
      ) : (
        /* List View */
        <>
          <div className={`${cardBg} rounded p-2 mb-1 border ${borderColor}`}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input placeholder="Search transfers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className={`w-full pl-8 pr-3 py-1 text-xs ${inputCls}`} data-testid="st-search" />
            </div>
          </div>
          <div className={`${cardBg} rounded border ${borderColor} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="st-list-table">
                <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                  <tr>
                    {['Sl', 'ST No', 'Date', 'Type', 'From', 'To', 'Items', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="px-3 py-6 text-center"><RefreshCw className="animate-spin mx-auto" size={18} /></td></tr>
                  ) : filteredList.map((row, idx) => (
                    <tr key={row.id} className={`border-t ${borderColor} hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50'}`}>
                      <td className="px-3 py-2">{idx + 1}</td>
                      <td className="px-3 py-2 font-mono">{row.stNo}</td>
                      <td className="px-3 py-2">{row.stDate}</td>
                      <td className="px-3 py-2">{row.transferType}</td>
                      <td className="px-3 py-2">{row.fromBranch}</td>
                      <td className="px-3 py-2">{row.toBranch}</td>
                      <td className="px-3 py-2">{row.items}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${row.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => setView('form')} className="p-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid={`st-edit-${row.id}`}><Edit2 size={11} /></button>
                          <button className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`st-delete-${row.id}`}><Trash2 size={11} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && filteredList.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">No transfers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StockTransferToShopScreen;
